import * as Y from 'yjs'
import * as awarenessProtocol from 'y-protocols/awareness'
import * as syncProtocol from 'y-protocols/sync'
import * as authProtocol from 'y-protocols/auth'
import { ObservableV2 } from 'lib0/observable'
import * as encoding from 'lib0/encoding'
import * as decoding from 'lib0/decoding'
import { collaborativeEdit as api, CursorInfo }  from 'st-app/api'
import { debounce, registerServerMessage } from 'common'

export interface SSEProviderOptions {
    id: number
    onPropertySaved?: (value: string) => void
}

type AwarenessUpdateEvent = {
    added: number[]
    updated: number[]
    removed: number[]
}

enum MessageType {
    SYNC = 0,
    QUERY_AWARENESS = 3,
    AWARENESS = 1,
    AUTH = 2,
}

type MessageHandler = (encoder: encoding.Encoder, decoder: decoding.Decoder, provider: SSEProvider, emitSynced: boolean, messageType: number) => void;
const messageHandlers: MessageHandler[] = []

messageHandlers[MessageType.SYNC] = (encoder, decoder, provider, emitSynced, messageType) => {
    encoding.writeVarUint(encoder, messageType)
    const syncMessageType = syncProtocol.readSyncMessage(decoder, encoder, provider.doc, provider);
    if (emitSynced && syncMessageType === syncProtocol.messageYjsSyncStep2 && !provider.synced) {
        provider.synced = true
    }
}

messageHandlers[MessageType.QUERY_AWARENESS] = (encoder, decoder, provider, emitSynced, messageType) => {
    encoding.writeVarUint(encoder, messageType)
    awarenessProtocol.encodeAwarenessUpdate(provider.awareness, [...provider.awareness.getStates().keys()])
}

messageHandlers[MessageType.AWARENESS] = (encoder, decoder, provider, emitSynced, messageType) => {
    awarenessProtocol.applyAwarenessUpdate(provider.awareness, decoding.readVarUint8Array(decoder), provider);
}

messageHandlers[MessageType.AUTH] = (encoder, decoder, provider, emitSynced, messageType) => {
    authProtocol.readAuthMessage(decoder, provider.doc, (_ydoc, reason) => permissionDeniedHandler(provider, reason));
}

const permissionDeniedHandler = (provider: SSEProvider, reason: any) => {
    console.warn(`Permission denied.\n${reason}`)
}

const providers: { [key: string]: SSEProvider[] } = {};

export class SSEProvider extends ObservableV2<{
    'connection-close': (event: CloseEvent | null, provider: SSEProvider) => any,
    'status': (event: { status: 'connected' | 'disconnected' | 'connecting' }) => any,
    'connection-error': (event: Event, provider: SSEProvider) => any,
    'sync': (state: boolean) => any,
}> {
    id: number
    doc: Y.Doc
    synced: boolean = false
    awareness: awarenessProtocol.Awareness
    connected: boolean = false
    onPropertySaved?: (value: string) => void
    key: string
    private resyncInterval: number | null = null

    constructor(key: string, doc: Y.Doc, options?: SSEProviderOptions) {
        super();

        this.id = options?.id ?? -1
        this.key = key
        this.doc = doc
        this.onPropertySaved = options?.onPropertySaved
        
        this.awareness = new awarenessProtocol.Awareness(this.doc)
        this.doc.on('update', this.updateHandler.bind(this));
        this.awareness.on('update', this.awarenessUpdateHandler.bind(this));
        this.connect();
    }

    private resync() {
        this.broadcastMessage(MessageType.SYNC, (encoder) => syncProtocol.writeSyncStep1(encoder, this.doc));
    }
    
    private broadcast(buf: Uint8Array) {
        if (this.connected) {
            const messageStr = String.fromCharCode.apply(null, [...buf])
            const messageBase64 = btoa(messageStr);
            api.Broadcast(this.key, messageBase64, this.doc.clientID.toString())
        }
    }

    processSSE(data: { Key: string, MessageBase64: string, SourceClientID: string }) {
        if (data.Key !== this.key)
            return;

        const messageStr = atob(data.MessageBase64);
        const buf = new Uint8Array(messageStr.length);
        for (let i = 0; i < messageStr.length; i++) {
            buf[i] = messageStr.charCodeAt(i);
        }

        const encoder = this.readMessage(buf, true);
        if (encoding.length(encoder) > 1) {
            this.broadcast(encoding.toUint8Array(encoder));
        }
    }

    readMessage = (buf: Uint8Array, emitSynced: boolean): encoding.Encoder => {
        const decoder = decoding.createDecoder(buf);
        const encoder = encoding.createEncoder();
        const messageType = decoding.readVarUint(decoder);
        const messageHandler = messageHandlers[messageType];
        if (messageHandler) {
            messageHandler(encoder, decoder, this, emitSynced, messageType);
        } else {
            console.warn(`Unknown message type: ${messageType}`)
        }
        return encoder;
    }

    updatePresence(user: CursorInfo | null) {
        this.awareness.setLocalStateField('user', user)
    }

    private updateHandler(update: Uint8Array, origin: any) {
        if (origin !== this) {
            this.broadcastMessage(MessageType.SYNC, (encoder) => syncProtocol.writeUpdate(encoder, update));
        }
    }

    private awarenessUpdateHandler({ added, updated, removed }: AwarenessUpdateEvent, _origin: any) {
        const changedClients = [...added, ...updated, ...removed];
        this.broadcastMessage(MessageType.AWARENESS, (encoder) =>encoding.writeVarUint8Array(encoder, awarenessProtocol.encodeAwarenessUpdate(this.awareness, changedClients)));
    }

    private broadcastMessage(type: MessageType, callback?: (encoder: encoding.Encoder) => void) {
        const buf = this.buildMessage(type, callback);
        this.broadcast(buf);
    }

    private buildMessage(type: MessageType, callback?: (encoder: encoding.Encoder) => void): Uint8Array {
        const encoder = encoding.createEncoder();
        encoding.writeVarUint(encoder, type);
        if (callback) {
            callback(encoder);
        }
        const buf = encoding.toUint8Array(encoder);
        return buf;
    }

    private connect() {
        if (!this.connected) {
            this.connected = true;
            providers[this.key] = [...providers[this.key] || [], this];
        }
        
        this.broadcastMessage(MessageType.SYNC, (encoder) => syncProtocol.writeSyncStep1(encoder, this.doc));
        this.broadcastMessage(MessageType.SYNC, (encoder) => syncProtocol.writeSyncStep2(encoder, this.doc));
        this.broadcastMessage(MessageType.QUERY_AWARENESS);
        this.broadcastMessage(MessageType.AWARENESS, (encoder) => encoding.writeUint8Array(encoder, awarenessProtocol.encodeAwarenessUpdate(this.awareness, [this.doc.clientID])));
        this.resyncInterval = window.setInterval(this.resync.bind(this), 1000);
    }

    private disconnect() {
        this.broadcastMessage(MessageType.AWARENESS, (encoder) => encoding.writeVarUint8Array(encoder, awarenessProtocol.encodeAwarenessUpdate(this.awareness, [this.doc.clientID], new Map())));

        if (this.connected) {
            this.connected = false;
            providers[this.key] = providers[this.key].filter(provider => provider !== this);
            if (providers[this.key].length === 0) {
                delete providers[this.key];
            }
        }

        if (this.resyncInterval !== null) {
            window.clearInterval(this.resyncInterval);
            this.resyncInterval = null;
        }
    }

    destroy() {
        this.disconnect();

        this.awareness.off('update', this.awarenessUpdateHandler.bind(this));
        this.doc.off('update', this.updateHandler.bind(this));
        super.destroy();
    }
}

registerServerMessage('CollaborativeEditBroadcastMessage', (data: { Key: string, MessageBase64: string, SourceClientID: string }) => {
    if (!providers[data.Key])
        return;

    for (const provider of providers[data.Key]) {
        provider.processSSE(data);
    }
});

registerServerMessage('PropertyUpdatedMessage', (data: { SrcName: string, Identity: string, Value: string }) => {
    const key = `${data.SrcName}:${data.Identity}`;
    if (!providers[key])
        return;

    for (const provider of providers[key]) {
        provider.onPropertySaved?.(data.Value);
    }
})

